import { ReactNode, useCallback, useEffect, useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { useToasts } from '@/hooks/useToasts'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Button,
  type ColorScheme,
  EditIcon,
  FeedItem,
  HStack,
  MultilineTextInput,
  SettingsIcon,
  Skeleton,
  Text,
  UtilityButton,
  VStack,
  tokenVar,
} from '@hivewatch/dark'

import {
  useCreateDeviceNoteMutation,
  useDeviceLogItemsQuery,
} from '@/graphql/generated/hooks'
import { DeviceLogItemType } from '@/graphql/generated/schemas'
import { BUTTON_PRESS, TAB_PRESS, trackEvent } from '@/utils/analytics'
import { formatDateTime } from '@/utils/formatDateTime'
import { roleName } from '@/utils/roles'

type DeviceLogItemData = {
  id: string
  type: DeviceLogItemType
  message: string
  title?: string | null
  createdAt: string
  user?: {
    firstName?: string | null
    lastName?: string | null
    roleName?: string | null
  } | null
}

interface IProps {
  deviceId: string
}

/**
 * Leading icon for a log entry: a gear for configuration changes, a pencil for
 * user notes — replacing FeedItem's default check glyph (ENG-32123).
 */
const getLogIcon = (type: DeviceLogItemType, colorScheme: ColorScheme) => {
  const color = tokenVar(
    colorScheme === 'light'
      ? 'colors.light.icon.secondary'
      : 'colors.icon.secondary'
  )
  return type === DeviceLogItemType.ConfigurationChange ? (
    <SettingsIcon color={color} size={20} />
  ) : (
    <EditIcon color={color} size={16} />
  )
}

/**
 * One device-log entry as a dark `FeedItem`: the title carries the actor +
 * action, the note carries the change/note body, and the leading icon carries
 * the system-vs-human distinction (no card shell or category chip).
 */
const DarkDeviceLogItem = ({
  logItem,
  colorScheme,
}: {
  logItem: DeviceLogItemData
  colorScheme: ColorScheme
}) => {
  const { t } = useTranslation()
  const prefix = colorScheme === 'light' ? 'light.' : ''

  const isConfigChange = logItem.type === DeviceLogItemType.ConfigurationChange
  const fullName =
    logItem.user && (logItem.user.firstName || logItem.user.lastName)
      ? `${logItem.user.firstName ?? ''} ${logItem.user.lastName ?? ''}`.trim()
      : 'Unknown User'
  const formattedRole = logItem.user?.roleName
    ? roleName(logItem.user.roleName)
    : 'User'
  const actionText = isConfigChange
    ? t('devices:drawer_logs_config_changed')
    : t('devices:drawer_logs_added_note')

  // Config changes carry a backend-authored title; notes build their own.
  const title =
    isConfigChange && logItem.title
      ? logItem.title
      : `${formattedRole} ${fullName} ${actionText}`

  return (
    <FeedItem
      colorScheme={colorScheme}
      icon={getLogIcon(logItem.type, colorScheme)}
      message={title}
      timestamp={formatDateTime(logItem.createdAt)}
    >
      {logItem.message ? (
        <Text color={`${prefix}text.secondary`} fontStyle='italic'>
          {logItem.message}
        </Text>
      ) : undefined}
    </FeedItem>
  )
}

/**
 * Rebrand (ENG-32123, Phase 2) Device Logs tab: a dark timeline `FeedItem` feed
 * plus a reskinned add-note affordance. Replaces the legacy card list.
 */
export const DarkDeviceLogs = ({ deviceId }: IProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const prefix = colorScheme === 'light' ? 'light.' : ''
  const { showError } = useToasts({ isRebrand: true, colorScheme })
  const [noteText, setNoteText] = useState('')
  const [isAddingNote, setIsAddingNote] = useState(false)
  const [createDeviceNote, { loading: isCreatingNote }] =
    useCreateDeviceNoteMutation()

  useEffect(() => {
    trackEvent(`${TAB_PRESS} Device Drawer - Device Logs`, {
      device_id: deviceId,
    })
  }, [deviceId])

  const {
    data,
    loading: isLoading,
    error,
    refetch,
  } = useDeviceLogItemsQuery({ variables: { deviceId }, skip: !deviceId })

  const handleCancelNote = useCallback(() => {
    setNoteText('')
    setIsAddingNote(false)
  }, [])

  const handleAddNote = useCallback(async () => {
    if (!noteText.trim()) return
    try {
      await createDeviceNote({
        variables: { input: { deviceId, message: noteText.trim() } },
      })
      trackEvent(`${BUTTON_PRESS} Device Drawer - Device Logs > Add Note`, {
        device_id: deviceId,
      })
      setNoteText('')
      setIsAddingNote(false)
      refetch()
    } catch {
      showError(t('devices:drawer_logs_add_note_error'))
    }
  }, [noteText, deviceId, createDeviceNote, refetch, showError, t])

  const addNote: ReactNode = isAddingNote ? (
    <VStack align='stretch' gap='8px'>
      <MultilineTextInput
        autoFocus
        colorScheme={colorScheme}
        onChange={(e) => setNoteText(e.target.value)}
        placeholder={t('devices:drawer_logs_note_placeholder')}
        value={noteText}
      />
      <Box display='flex' gap='8px' justifyContent='flex-end'>
        <Button
          colorScheme={colorScheme}
          onClick={handleCancelNote}
          variant='secondary'
        >
          {t('common:cancel')}
        </Button>
        <Button
          colorScheme={colorScheme}
          disabled={!noteText.trim()}
          isLoading={isCreatingNote}
          onClick={handleAddNote}
          variant='primary'
        >
          {t('common:add_note')}
        </Button>
      </Box>
    </VStack>
  ) : (
    <HStack alignItems='center' gap='8px'>
      <UtilityButton
        colorScheme={colorScheme}
        icon={<EditIcon />}
        label={t('common:add_note')}
        onClick={() => setIsAddingNote(true)}
        tooltipLabel={t('common:add_note')}
      />
      <Text color={`${prefix}text.secondary`}>
        {t('devices:drawer_logs_add_note_text')}
      </Text>
    </HStack>
  )

  const logItems = (data?.deviceLogItems?.edges
    ?.map((edge) => edge?.node)
    .filter(Boolean) ?? []) as DeviceLogItemData[]

  return (
    <Box px='24px' py='16px'>
      <Box pb='16px'>{addNote}</Box>
      <Box
        borderBottomWidth='1px'
        borderColor={`${prefix}border.subtle`}
        borderStyle='solid'
        mb='16px'
      />

      {isLoading ? (
        <VStack align='stretch' gap='8px' mx='-12px'>
          <Skeleton borderRadius='8px' h='72px' />
          <Skeleton borderRadius='8px' h='72px' />
          <Skeleton borderRadius='8px' h='72px' />
        </VStack>
      ) : error ? (
        <Box color={`${prefix}status.error-text`} py='16px' textAlign='center'>
          {t('devices:drawer_logs_error')}
        </Box>
      ) : logItems.length === 0 ? (
        <Box color={`${prefix}text.secondary`} py='16px' textAlign='center'>
          {t('devices:drawer_logs_empty')}
        </Box>
      ) : (
        <VStack align='stretch' gap='0' mx='-12px'>
          {logItems.map((logItem) => (
            <DarkDeviceLogItem
              colorScheme={colorScheme}
              key={logItem.id}
              logItem={logItem}
            />
          ))}
        </VStack>
      )}
    </Box>
  )
}
